import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

/**
 *  KÖNYVELT BIZONYLAT KÖTÉSE EGY TÁRGYI ESZKÖZHÖZ – bérleti díj (bevétel) vagy nem aktivált kiadás.
 *
 *  Az egyszeres (naplófőkönyv) és a kettős könyvelés is ezt nyitja meg egy mentett tételen. A
 *  teljes bizonylat köthető, vagy – ha a számla a NAV-tól jött, és megvannak a sorai – csak
 *  kiválasztott sorai (gyakori, hogy a számlának csak egy sora a bérleti díj). Az összeg kézzel is
 *  átírható. A kötés a Tárgyi eszköz modul megtérülési kimutatásába kerül.
 */
@Component({
  selector: 'app-eszkoz-hozam-kotes',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './hozam-kotes.component.html',
  //  A NAPLO közös stílusa (gombok, táblázat, mezők) – a komponens a NAPLO és a kettős képernyőn is fut.
  styleUrls: ['../../naplo/naplo-shared.scss', './hozam-kotes.component.scss'],
})
export class EszkozHozamKotesComponent implements OnChanges {
  @Input() ugyfelId: string | null = null;
  @Input() tetelId: string | null = null;
  @Output() kesz = new EventEmitter<boolean>();

  betolt = false;
  hiba: string | null = null;
  adat: any = null;
  eszkozId: string | null = null;
  tipus: 'bevetel' | 'kiadas' = 'bevetel';
  mod: 'egesz' | 'sorok' = 'egesz';
  kijelolt = new Set<string>();
  osszeg = { netto: 0, afa: 0, brutto: 0 };
  megnevezes = '';
  mentes = false;

  constructor(private http: HttpClient) {}

  async ngOnChanges() {
    this.adat = null; this.hiba = null; this.kijelolt.clear();
    if (!this.ugyfelId || !this.tetelId) return;
    this.betolt = true;
    try {
      this.adat = await firstValueFrom(this.http.get<any>(`/api/eszkoz/hozam/bizonylat/${this.ugyfelId}/${this.tetelId}`));
      this.tipus = this.adat.javasolt === 'kiadas' ? 'kiadas' : 'bevetel';
      const aktiv = (this.adat.eszkozok || []).filter((e: any) => !e.kivezetes_datum);
      this.eszkozId = aktiv.length === 1 ? aktiv[0].id : null;
      this.mod = 'egesz';
      this.osszeg = { netto: this.adat.tetel.netto || 0, afa: this.adat.tetel.afa || 0, brutto: this.adat.tetel.brutto || 0 };
      this.megnevezes = this.adat.tetel.megnevezes || this.adat.tetel.partner_nev || '';
    } catch (e: any) {
      this.hiba = e?.status === 403 ? 'Ehhez a Tárgyi eszköz modulhoz is jogosultság kell.' : (e?.error?.error || 'Nem sikerült betölteni a bizonylatot.');
    } finally { this.betolt = false; }
  }

  /** Már valamelyik eszközhöz kötött NAV-sor – azt nem ajánljuk fel újra. */
  sorKotve(sorszam: string): any { return (this.adat?.kotesek || []).find((k: any) => k.osa_sor === sorszam); }
  sorValt(s: any, be: boolean) { if (be) this.kijelolt.add(s.sorszam); else this.kijelolt.delete(s.sorszam); }
  get kijeloltSorok(): any[] { return (this.adat?.nav_sorok || []).filter((s: any) => this.kijelolt.has(s.sorszam)); }
  get kijeloltOsszeg(): number { return this.kijeloltSorok.reduce((n, s) => n + (this.adat?.afa_alany ? s.netto : s.brutto), 0); }
  /** Az egész bizonylatra már van kötés (sor nélkül) – figyelmeztetünk a duplázás ellen. */
  get egeszKotve(): any[] { return (this.adat?.kotesek || []).filter((k: any) => !k.osa_sor); }

  get mentheto(): boolean {
    if (!this.eszkozId || this.mentes) return false;
    return this.mod === 'sorok' ? this.kijelolt.size > 0 : (Number(this.osszeg.netto) || Number(this.osszeg.brutto)) !== 0;
  }

  async ment() {
    if (!this.mentheto) return;
    this.mentes = true; this.hiba = null;
    try {
      const alap = { eszkoz_id: this.eszkozId, tipus: this.tipus, tetel_id: this.tetelId, datum: this.adat.tetel.datum };
      const body = this.mod === 'sorok'
        ? { ...alap, sorok: this.kijeloltSorok.map((s) => ({ osa_sor: s.sorszam, megnevezes: s.megnevezes, netto: s.netto, afa: s.afa, brutto: s.brutto })) }
        : { ...alap, megnevezes: this.megnevezes, netto: this.osszeg.netto, afa: this.osszeg.afa, brutto: this.osszeg.brutto };
      await firstValueFrom(this.http.post(`/api/eszkoz/hozam/${this.ugyfelId}`, body));
      this.kesz.emit(true);
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült menteni.'; }
    finally { this.mentes = false; }
  }

  async kotesTorol(k: any) {
    try {
      await firstValueFrom(this.http.delete(`/api/eszkoz/hozam/${this.ugyfelId}/${k.id}`));
      await this.ngOnChanges();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült törölni.'; }
  }
}
